import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowLeft, Check, Loader2, Plus, Sparkles } from 'lucide-react'
import { proposeScope } from '@/api/generateapp'
import type { WireAttachment } from '@/lib/attachments'
import type { DiscoveryGuide, ScopeDecisions, ScopeProposal, ScopeQuestion } from '@/types'

// ---------------------------------------------------------------------------
// Guided start: the questions and the plan — for users who are not sure how
// to describe an app. The AI proposes a few questions chosen for the kind of
// app picked (POST /generateapp/propose-scope with the catalogue ids, or
// `unsure` when nothing was picked), each with a suggested answer, asked ONE
// AT A TIME beside a "Your app so far" panel that grows with every answer;
// then a plain-language plan — app name, answers (each changeable), the
// defaults it assumed, and "Anything else?" — before Build. The answers
// become the same ScopeDecisions the pre-FRS scope round produces, so the
// build honours them exactly and skips asking its own scope round again.
// ---------------------------------------------------------------------------

interface Props {
  description: string
  guide: DiscoveryGuide
  /** "Started from: …" — opens the "Your app so far" text. */
  title: string
  attachments: WireAttachment[]
  building: boolean
  /** "Anything else?" already written (fill in the blanks) — the plan keeps it. */
  initialNotes?: string
  appName: string
  onAppNameChange: (name: string) => void
  /** Back to the description, with the "Anything else?" notes as they stand. */
  onBack: (notes: string) => void
  /** null = build without the questions (the proposal could not be made). */
  onBuild: (decisions: ScopeDecisions | null) => void
}

type Picks = Record<string, { selected: string[]; custom: string[] }>

const initialPicks = (p: ScopeProposal): Picks => Object.fromEntries(
  p.questions.map((q) => [q.id, {
    selected: q.options.filter((o) => o.selected).map((o) => o.label),
    custom: [],
  }]),
)

export default function GuidedQuestions({
  description, guide, title, attachments, building, initialNotes = '', appName, onAppNameChange, onBack, onBuild,
}: Props) {
  const [proposal, setProposal] = useState<ScopeProposal | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState(0)
  const [picks, setPicks] = useState<Picks>({})
  const [assumed, setAssumed] = useState<Record<number, string>>({})
  const [adding, setAdding] = useState('')
  const [notes, setNotes] = useState(initialNotes)
  const [fresh, setFresh] = useState(-1)

  useEffect(() => {
    let cancelled = false
    proposeScope({ description, guide, attachments })
      .then((p: ScopeProposal) => {
        if (cancelled) return
        if (!p?.questions?.length) {
          setError('I could not come up with questions for this one.')
          return
        }
        setProposal(p)
        setPicks(initialPicks(p))
        setAssumed(Object.fromEntries((p.assumptions || []).map((a, i) => [i, a.label])))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('guided propose-scope failed', err)
        const status = (err as { response?: { status?: number } })?.response?.status
        // 402 (no AI access) is surfaced by the global client handler.
        if (status === 402) { onBack(initialNotes); return }
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setError(detail || 'I could not prepare the questions right now.')
      })
    return () => { cancelled = true }
    // Run once per mount — the parent remounts this for a new description.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const questions = proposal?.questions || []
  const atPlan = proposal !== null && step >= questions.length
  const current: ScopeQuestion | undefined = questions[step]
  // What the AI suggested for each question, so the option keeps its
  // "Suggested" tag after the user changes the pick.
  const suggested = useMemo(() => new Set(
    questions.flatMap((q) => q.options.filter((o) => o.selected).map((o) => `${q.id}\u0000${o.label}`)),
  ), [questions])

  const chosenOf = (q: ScopeQuestion): string[] => {
    const p = picks[q.id]
    return [...(p?.selected || []), ...(p?.custom || [])]
  }
  const phrase = (q: ScopeQuestion): string | null => {
    const chosen = chosenOf(q)
    return chosen.length ? `${q.header}: ${chosen.join(', ')}.` : null
  }
  const said = useMemo(
    () => questions.slice(0, step).map(phrase).filter((s): s is string => !!s),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, step, picks],
  )

  const toggle = (q: ScopeQuestion, labelText: string) => {
    setPicks((prev) => {
      const cur = prev[q.id] || { selected: [], custom: [] }
      const on = cur.selected.includes(labelText)
      const selected = q.multi
        ? (on ? cur.selected.filter((l) => l !== labelText) : [...cur.selected, labelText])
        : [labelText]
      return { ...prev, [q.id]: { selected, custom: q.multi ? cur.custom : [] } }
    })
  }
  const addCustom = (q: ScopeQuestion) => {
    const v = adding.trim()
    if (!v) return
    setPicks((prev) => {
      const cur = prev[q.id] || { selected: [], custom: [] }
      if (cur.custom.includes(v) || cur.selected.includes(v)) return prev
      // A typed answer on a single choice replaces the pick.
      return { ...prev, [q.id]: { selected: q.multi ? cur.selected : [], custom: q.multi ? [...cur.custom, v] : [v] } }
    })
    setAdding('')
  }
  const removeCustom = (q: ScopeQuestion, v: string) => {
    setPicks((prev) => {
      const cur = prev[q.id]
      if (!cur) return prev
      return { ...prev, [q.id]: { ...cur, custom: cur.custom.filter((c) => c !== v) } }
    })
  }
  const next = () => {
    // The new phrase lands at the end of "Your app so far" — highlight it.
    if (current && phrase(current)) setFresh(said.length)
    else setFresh(-1)
    setAdding('')
    setStep((s) => s + 1)
  }
  const goTo = (i: number) => { setFresh(-1); setAdding(''); setStep(i) }

  const decisions = (): ScopeDecisions => {
    const answers = questions.map((q) => {
      const p = picks[q.id] || { selected: [], custom: [] }
      return {
        id: q.id,
        question: q.question,
        selected: p.selected,
        // Only multi-select questions carry exclusions (same rule as the
        // pre-FRS ScopeConfirmation) — an unchosen radio is not declined scope.
        deselected: q.multi ? q.options.map((o) => o.label).filter((l) => !p.selected.includes(l)) : [],
        custom: p.custom,
      }
    })
    const accepted = (proposal?.assumptions || []).map((a, i) => assumed[i] || a.label)
    if (accepted.length) {
      answers.push({
        id: 'assumptions',
        question: 'Defaults the user reviewed and accepted',
        selected: accepted,
        deselected: [],
        custom: [],
      })
    }
    return { answers, additional_notes: notes.trim() }
  }

  // ── loading / error ─────────────────────────────────────────────────
  if (!proposal) {
    return (
      <div style={{ ...panel, gap: 14 }}>
        <BackLink onClick={() => onBack(notes)} disabled={building} />
        {error ? (
          <>
            <b>{error}</b>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9625rem' }}>
              You can still build from the description — the builder will ask what it needs on the way.
            </span>
            <div style={actions}>
              <button type="button" className="btn btn-primary" onClick={() => onBuild(null)} disabled={building}>
                {building ? <Spin /> : <Sparkles size={16} />} Build it anyway
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => onBack(notes)} disabled={building}>
                Edit the description
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--color-text-secondary)' }}>
            <Spin /> Picking the few questions that matter for this app…
          </div>
        )}
      </div>
    )
  }

  // ── the plan ────────────────────────────────────────────────────────
  if (atPlan) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <BackLink label="Back to the questions" onClick={() => goTo(Math.max(0, questions.length - 1))} disabled={building} />
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 380px', minWidth: 0 }}>
            <h1 style={h1}>Here's the plan</h1>
            <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)' }}>
              Read it like a brief. Change anything now — or build and keep refining afterwards in chat.
            </p>
          </div>
          <div style={{ flex: '0 1 300px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="guided-app-name" style={fieldLabel}>
              App name <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(optional)</span>
            </label>
            <input id="guided-app-name" value={appName} disabled={building} onChange={(e) => onAppNameChange(e.target.value)}
              placeholder="We'll name it from the description" style={textInput} />
          </div>
        </div>

        {proposal.summary && (
          <section style={panel}>
            <h2 style={h2}>What we'll build first</h2>
            <p style={{ margin: 0, lineHeight: 1.6 }}>{proposal.summary}</p>
          </section>
        )}

        <section style={panel}>
          <h2 style={h2}>Your answers</h2>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {questions.map((q, i) => {
              const chosen = chosenOf(q)
              return (
                <div key={q.id} style={row}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.9025rem', color: 'var(--color-text-secondary)' }}>{q.header}</div>
                    <div>{chosen.length ? chosen.join(', ') : <span style={{ color: 'var(--color-text-faint)' }}>Nothing picked</span>}</div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={building} onClick={() => goTo(i)}>Change</button>
                </div>
              )
            })}
          </div>
        </section>

        {(proposal.assumptions || []).length > 0 && (
          <section style={panel}>
            <div>
              <h2 style={h2}>We assumed these</h2>
              <p style={{ margin: '2px 0 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)' }}>
                You didn't mention them, so we picked the usual choice. Change any that's wrong.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(proposal.assumptions || []).map((a, i) => (
                <div key={a.label} style={row}>
                  {a.alternatives.length === 0 && <span style={{ minWidth: 0 }}>{a.label}</span>}
                  {a.alternatives.length > 0 && (
                    <select
                      aria-label={`Change: ${a.label}`}
                      value={assumed[i] || a.label}
                      disabled={building}
                      onChange={(e) => setAssumed((prev) => ({ ...prev, [i]: e.target.value }))}
                      style={selectStyle}
                    >
                      {[a.label, ...a.alternatives].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <section style={panel}>
          <div>
            <label htmlFor="guided-notes" style={{ ...h2, display: 'block', color: 'var(--color-text)' }}>
              Anything else? <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>(optional)</span>
            </label>
            <p id="guided-notes-help" style={{ margin: '2px 0 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)' }}>
              Something the plan missed? Say it in your own words and it's added to the brief before building.
            </p>
          </div>
          <textarea
            id="guided-notes"
            aria-describedby="guided-notes-help"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={building}
            placeholder="e.g. Each branch has its own team. Show request status to the person who raised it."
            rows={3}
            style={notesStyle}
          />
        </section>

        <div style={buildBar}>
          <span style={{ fontSize: '0.9425rem', color: 'var(--color-text-secondary)' }}>
            You'll watch each screen appear as it's built.
          </span>
          <div style={actions}>
            <button type="button" className="btn btn-secondary" onClick={() => onBack(notes)} disabled={building}>Edit description</button>
            <button type="button" className="btn btn-primary" onClick={() => onBuild(decisions())} disabled={building}>
              {building ? <Spin /> : <Sparkles size={16} />} {building ? 'Setting up…' : 'Build my app'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── one question at a time ──────────────────────────────────────────
  const q = current as ScopeQuestion
  const p = picks[q.id] || { selected: [], custom: [] }
  const answeredBefore = questions.slice(0, step)
  const toCome = questions.slice(step + 1).map((x) => x.header.toLowerCase())
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <BackLink label="Edit the description" onClick={() => onBack(notes)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.8825rem', color: 'var(--color-text-secondary)' }}>Question {step + 1} of {questions.length}</span>
          <span style={{ display: 'flex', gap: 4 }} role="img" aria-label={`Question ${step + 1} of ${questions.length}`}>
            {questions.map((qq, i) => (
              <i key={qq.id} style={{ width: 24, height: 4, borderRadius: 2, background: i <= step ? 'var(--color-accent)' : 'var(--color-border-strong)' }} />
            ))}
          </span>
        </div>
        <button type="button" onClick={() => goTo(questions.length)} style={linkBtn}>Skip the rest — use suggestions</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <section aria-labelledby="guided-q" style={{ ...panel, flex: '999 1 480px', minWidth: 0, padding: 26, gap: 18 }}>
          {step === 0 && proposal.summary && (
            <div style={{ fontSize: '0.9625rem', color: 'var(--color-text-secondary)' }}>{proposal.summary}</div>
          )}
          <div>
            <span style={{ fontSize: '0.8625rem', fontWeight: 500, color: 'var(--color-accent)' }}>{q.header}</span>
            <h1 id="guided-q" style={{ ...h1, fontSize: '1.5125rem', marginTop: 4 }}>{q.question}</h1>
            <p style={{ margin: '6px 0 0', fontSize: '0.9625rem', color: 'var(--color-text-secondary)' }}>
              {q.why ? `${q.why} ` : ''}{q.multi ? 'Pick all that apply.' : 'Pick one.'}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {q.options.map((o) => {
              const on = p.selected.includes(o.label)
              return (
                <button key={o.label} type="button" aria-pressed={on} onClick={() => toggle(q, o.label)} style={optionCard(on)}>
                  <span style={tick(on, q.multi)} aria-hidden="true">{on && <Check size={13} color="#fff" strokeWidth={3} />}</span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 500 }}>
                      {o.label}
                      {suggested.has(`${q.id}\u0000${o.label}`) && <span style={suggestedTag}>Suggested</span>}
                    </span>
                    {o.description && <span style={{ fontSize: '0.9225rem', color: 'var(--color-text-secondary)' }}>{o.description}</span>}
                  </span>
                </button>
              )
            })}
            {p.custom.map((c) => (
              <button key={`c-${c}`} type="button" aria-pressed title="Remove" onClick={() => removeCustom(q, c)} style={optionCard(true)}>
                <span style={tick(true, q.multi)} aria-hidden="true"><Check size={13} color="#fff" strokeWidth={3} /></span>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{c}</span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="guided-own" style={fieldLabel}>Or say it your way</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                id="guided-own"
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(q) } }}
                placeholder="Type your own answer"
                style={{ ...textInput, flex: 1, minWidth: 0 }}
              />
              <button type="button" className="btn btn-secondary" onClick={() => addCustom(q)} disabled={!adding.trim()}>
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
          <div style={{ ...actions, justifyContent: 'space-between' }}>
            <button type="button" className="btn btn-secondary" onClick={() => (step > 0 ? goTo(step - 1) : onBack(notes))}>Back</button>
            <button type="button" className="btn btn-primary" onClick={next}>
              {step + 1 < questions.length ? 'Next question' : 'See the plan'}
            </button>
          </div>
        </section>

        <aside aria-label="Your app so far" style={{ ...panel, flex: '1 1 280px', minWidth: 0, gap: 14 }}>
          <div>
            <h2 style={{ ...h2, fontSize: '0.9225rem', color: 'var(--color-text-secondary)' }}>Your app so far</h2>
            <p aria-live="polite" style={{ margin: '8px 0 0', lineHeight: 1.6 }}>
              {title}.
              {said.map((s, i) => (
                <span key={i}> <span style={i === fresh ? freshStyle : undefined}>{s}</span></span>
              ))}
            </p>
          </div>
          {answeredBefore.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              {answeredBefore.map((aq, i) => (
                <div key={aq.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.9425rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--color-text-secondary)' }}>{aq.header}</div>
                    <div>{chosenOf(aq).join(', ') || '—'}</div>
                  </div>
                  <button type="button" onClick={() => goTo(i)} style={linkBtn}>Change</button>
                </div>
              ))}
            </div>
          )}
          {toCome.length > 0 && (
            <p style={{ margin: 0, fontSize: '0.8825rem', color: 'var(--color-text-faint)' }}>Still to come: {toCome.join(', ')}.</p>
          )}
        </aside>
      </div>
    </div>
  )
}

function Spin() {
  return <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
}

function BackLink({ onClick, label = 'Back to the description', disabled }: {
  onClick: () => void; label?: string; disabled?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={backLink}>
      <ArrowLeft size={14} /> {label}
    </button>
  )
}

// ── styles ────────────────────────────────────────────────────────────
const panel: CSSProperties = {
  background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 14,
  padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10,
}
const h1: CSSProperties = { margin: 0, fontSize: '1.9625rem', lineHeight: 1.25, fontWeight: 600 }
const h2: CSSProperties = { margin: 0, fontSize: '1.0625rem', fontWeight: 600 }
const fieldLabel: CSSProperties = { fontSize: '0.9025rem', fontWeight: 500, color: 'var(--color-text-secondary)' }
const actions: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }
const row: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  padding: '10px 0', borderTop: '1px solid var(--color-border)',
}
const freshStyle: CSSProperties = {
  background: 'var(--color-warning-weak)', color: 'var(--color-text)', borderRadius: 4, padding: '0 3px',
}
const backLink: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', background: 'none', border: 0,
  padding: '6px 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)', cursor: 'pointer',
}
const linkBtn: CSSProperties = {
  background: 'none', border: 0, padding: '4px 0', font: 'inherit', fontSize: '0.9425rem',
  color: 'var(--color-accent)', cursor: 'pointer', flex: 'none',
}
const optionCard = (on: boolean): CSSProperties => ({
  font: 'inherit', display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', padding: '12px 14px',
  borderRadius: 12, textAlign: 'left', cursor: 'pointer', color: 'var(--color-text)',
  border: `1.5px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
  background: on ? 'var(--color-accent-weak)' : 'var(--color-bg)',
})
const tick = (on: boolean, multi: boolean): CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', width: 20, height: 20,
  marginTop: 1, borderRadius: multi ? 6 : 999,
  border: `1.5px solid ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-accent)' : 'var(--color-bg)',
})
const suggestedTag: CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-success)', background: 'var(--color-success-weak)',
  padding: '1px 8px', borderRadius: 999,
}
const textInput: CSSProperties = {
  height: 42, padding: '0 12px', background: 'var(--color-input-bg, var(--color-bg))',
  border: '1px solid var(--color-border-strong)', borderRadius: 10, color: 'var(--color-text)', font: 'inherit',
  fontSize: '1.0125rem',
}
const selectStyle: CSSProperties = {
  width: 'auto', minWidth: 260, fontSize: '0.9825rem', padding: '7px 2rem 7px 10px', background: 'var(--color-input-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border-strong)', borderRadius: 8, maxWidth: '100%',
}
const notesStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', minHeight: 84, padding: '10px 12px',
  background: 'var(--color-input-bg, var(--color-bg))', border: '1px solid var(--color-border-strong)',
  borderRadius: 10, color: 'var(--color-text)', fontSize: '1.0125rem', lineHeight: 1.5, resize: 'vertical',
  fontFamily: 'var(--font-primary)',
}
const buildBar: CSSProperties = {
  position: 'sticky', bottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  flexWrap: 'wrap', padding: '14px 18px', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 14, boxShadow: '0 8px 24px var(--color-shadow, rgba(0,0,0,.08))',
}
